import { Asset } from '@/api/generatedApi';
import { BodyText } from '@/components/BodyText';
import { Thumbnail } from '@/components/Thumbnail';
import { AddToCompareButton } from '@/components/CompareCart';
import { Box, Button } from '@mui/material';
import { West, East, VisibilityOff } from '@mui/icons-material';
import { HighlightedText } from './HighlightedText';
import { getAssetValue } from '../utils/getAssetValue';
import { AssetThumbnailContainer, ThumbnailWrapper } from './SuitableSubstitutesTable';

export interface CellRendererConfig {
  searchQuery?: string;
  selectedAssetIds: string[];
  onPreviousOption?: () => void;
  onNextOption?: () => void;
  onToggleSelection?: (assetId: string) => void;
  onHideAsset?: (assetId: string) => void;
}

export const CellRenderer = ({
  asset,
  rowKey,
  isSearchAsset = false,
  config,
}: {
  asset: Asset;
  rowKey: string;
  isSearchAsset?: boolean;
  config: CellRendererConfig;
}) => {
  const {
    searchQuery = '',
    selectedAssetIds = [],
    onPreviousOption,
    onNextOption,
    onToggleSelection,
    onHideAsset,
  } = config;

  const isSelected = selectedAssetIds.includes(asset.id);

  switch (rowKey) {
    case 'thumbnail': {
      return (
        <AssetThumbnailContainer>
          <ThumbnailWrapper>
            <Thumbnail asset={asset} size={164} />
          </ThumbnailWrapper>
          <Box position='relative' display='flex' width='100%' justifyContent='flex-start' gap={1}>
            {isSearchAsset ? (
              <>
                <Button
                  variant='text'
                  size='small'
                  onClick={onPreviousOption}
                  sx={theme => ({
                    outline: `1px solid ${theme.palette.divider}`,
                    minWidth: '36px',
                    minHeight: '32px',
                    padding: '0 12px',
                  })}
                >
                  <West sx={{ fontSize: '18px', margin: '0 3px', color: 'text.secondary' }} />
                </Button>
                <Button
                  variant='text'
                  size='small'
                  onClick={onNextOption}
                  sx={theme => ({
                    outline: `1px solid ${theme.palette.divider}`,
                    minWidth: '36px',
                    minHeight: '32px',
                    padding: '0 12px',
                  })}
                >
                  <East sx={{ fontSize: '18px', margin: '0 3px', color: 'text.secondary' }} />
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant='text'
                  size='small'
                  onClick={() => onToggleSelection?.(asset.id)}
                  sx={theme => ({
                    outline: `1px solid ${isSelected ? theme.palette.secondary.main : theme.palette.divider}`,
                    minWidth: '36px',
                    minHeight: '32px',
                    padding: '0 12px',
                    backgroundColor: isSelected ? theme.palette.secondary.main : theme.palette.background.paper,
                    color: isSelected ? 'white !important' : `${theme.palette.text.secondary} !important`,
                    '&:hover': {
                      backgroundColor: isSelected ? theme.palette.secondary.main : theme.palette.background.paper,
                    },
                  })}
                >
                  Select
                </Button>
                <Button
                  variant='text'
                  size='small'
                  onClick={() => onHideAsset?.(asset.id)}
                  disabled={isSelected}
                  sx={theme => ({
                    outline: `1px solid ${theme.palette.divider}`,
                    minWidth: '32px',
                    minHeight: '32px',
                    padding: '0 12px',
                    backgroundColor: theme.palette.background.paper,
                    color: `${theme.palette.text.secondary} !important`,
                    '&:hover': {
                      backgroundColor: theme.palette.background.paper,
                    },
                  })}
                >
                  <VisibilityOff
                    sx={{
                      fontSize: '18px',
                      color: isSelected ? 'text.disabled' : undefined,
                    }}
                  />
                </Button>
              </>
            )}
            <AddToCompareButton asset={asset} variant='icon' />
          </Box>
        </AssetThumbnailContainer>
      );
    }
    case 'name':
    case 'created':
    case 'createdBy':
    case 'folder': {
      const value = getAssetValue(asset, rowKey);
      return (
        <BodyText>
          <HighlightedText text={value} searchQuery={searchQuery} />
        </BodyText>
      );
    }
    default: {
      if (rowKey.startsWith('metadata_')) {
        const metadataValue = getAssetValue(asset, rowKey);
        return (
          <BodyText>
            <HighlightedText text={metadataValue} searchQuery={searchQuery} />
          </BodyText>
        );
      }
      return;
    }
  }
};
